import type { CustomerFormValues } from '../../../types';

/** Housing Status Options */
export const HOUSING_STATUS_OPTIONS = [
    { label: 'Mortgage', value: 'Mortgage' },
    { label: 'Rent', value: 'Rent' },
    { label: 'Family', value: 'Family' },
    { label: 'Own Outright', value: 'OwnOutRight' },
    { label: 'Military', value: 'Military' },
    { label: 'Other', value: 'Other' }
];

/** Employment Status Options */
export const EMPLOYMENT_STATUS_OPTIONS = [
    { label: 'Employed', value: 'Employed' },
    { label: 'Unemployed', value: 'Unemployed' },
    { label: 'Retired', value: 'Retired' },
    { label: 'Active Military', value: 'ActiveMilitary' },
    { label: 'Retired Military', value: 'RetiredMilitary' },
    { label: 'Self Employed', value: 'SelfEmployed' },
    { label: 'Student', value: 'Student' },
    { label: 'Other', value: 'Other' }
];

/** Pay Frequency Options */
export const PAY_FREQUENCY_OPTIONS = [
    { label: 'Weekly', value: 'Weekly' },
    { label: 'Bi-Weekly', value: 'Biweekly' },
    { label: 'Monthly', value: 'Monthly' },
    { label: 'Annually', value: 'Annually' }
];

/** Field Constants - Previous Address */
export const YEARS_AT_PREVIOUS_ADDRESS_FIELD = 'yearsAtPreviousAddress';
export const MONTHS_AT_PREVIOUS_ADDRESS_FIELD = 'monthsAtPreviousAddress';

/** Field Constants - Additional Income */
export const OTHER_INCOME_FIELD = 'financialInfo.otherIncome';
export const OTHER_INCOME_SOURCE_FIELD = 'financialInfo.otherIncomeSource';

/**
 * Determines if housing & employment accordion should be expanded by default.
 * Checks if any housing or employment fields have values.
 */
export const hasHousingAndEmploymentData = (formValues: CustomerFormValues): boolean => {
    const housingInfo = formValues.housingInfo as Record<string, unknown> | undefined;
    const previousAddress = formValues.previousAddress as Record<string, unknown> | undefined;
    const currentEmployment = formValues.currentEmployment as Record<string, unknown> | undefined;
    const previousEmployment = formValues.previousEmployment as Record<string, unknown> | undefined;
    const financialInfo = formValues.financialInfo as Record<string, unknown> | undefined;

    return hasAnyFieldValue(
        housingInfo?.housingStatus,
        housingInfo?.mortgageOrRentAmount,
        formValues.yearsAtCurrentAddress,
        formValues.monthsAtCurrentAddress,
        previousAddress?.streetAddress,
        previousAddress?.city,
        previousAddress?.state,
        previousAddress?.zip,
        formValues.yearsAtPreviousAddress,
        formValues.monthsAtPreviousAddress,
        currentEmployment?.status,
        currentEmployment?.employerName,
        currentEmployment?.occupation,
        previousEmployment?.status,
        previousEmployment?.employerName,
        previousEmployment?.occupation,
        financialInfo?.otherIncome,
        financialInfo?.otherIncomeSource
    );
};

/**
 * Checks if any of the provided values are non-empty/non-null/non-undefined.
 * Used for accordion expansion logic.
 */
export const hasAnyFieldValue = (...values: unknown[]): boolean => {
    return values.some((value) => value !== undefined && value !== null && value !== '');
};

/**
 * Checks if employment data exists for a given section.
 */
export const hasEmploymentData = (employmentData: Record<string, unknown> | undefined): boolean => {
    if (!employmentData) return false;
    return Object.values(employmentData).some((value) => value !== undefined && value !== null && value !== '');
};

/**
 * Checks if previous address has any data.
 */
export const hasPreviousAddressData = (
    previousAddress: Record<string, unknown> | undefined,
    yearsAtPrevious?: number,
    monthsAtPrevious?: number
): boolean => {
    if (!previousAddress && !yearsAtPrevious && !monthsAtPrevious) return false;
    const hasAddress = Object.values(previousAddress || {}).some((value) => value !== undefined && value !== null && value !== '');
    const hasDuration = Number(yearsAtPrevious) > 0 || Number(monthsAtPrevious) > 0;
    return hasAddress || hasDuration;
};
